import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

export default function WhatToOfferDrawer({ company, isDarkMode, onClose, initialData }) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState('');
  const [minimized, setMinimized] = useState(false);

  // Pokud máme data předem načtená z tlačítka, použij je rovnou
  useEffect(() => {
    if (initialData) {
      setSuggestion(initialData.suggestion || '');
      setActivities(initialData.activities || []);
      setError(initialData.error || '');
    } else if (company?.ico) {
      fetchSuggestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSuggestion = async () => {
    setLoading(true);
    setError('');
    setSuggestion('');
    setActivities([]);
    try {
      const res = await fetch(`${API_URL}/ai-offer-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ico: company.ico, companyName: company.name })
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestion(data.suggestion || '');
        setActivities(data.activities || []);
      } else {
        const err = await res.json();
        setError(err.error || 'Chyba při načítání návrhu.');
      }
    } catch {
      setError('Nepodařilo se připojit k serveru.');
    } finally {
      setLoading(false);
    }
  };

  // Render suggestion text with basic bullet formatting
  // Render inline **bold** and *italic* markers as React elements
  const renderInline = (text) => {
    const parts = [];
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let last = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));
      if (match[0].startsWith('**')) {
        parts.push(<strong key={match.index}>{match[2]}</strong>);
      } else {
        parts.push(<em key={match.index}>{match[3]}</em>);
      }
      last = match.index + match[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  const renderSuggestion = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="h-2" />;

      // Nadpis: celý řádek je **...** nebo končí na :
      if (/^\*\*.*\*\*[:\s]*$/.test(trimmed) || (trimmed.endsWith(':') && !trimmed.startsWith('•') && !trimmed.startsWith('-'))) {
        const label = trimmed.replace(/^\*\*|\*\*[:\s]*$|:$/g, '').trim();
        return (
          <p key={i} className="font-bold mt-4 mb-1 text-sm uppercase tracking-wide text-blue-600">
            {label}
          </p>
        );
      }

      // Odrážka: • nebo - nebo * (ale ne **)
      if (trimmed.startsWith('•') || trimmed.startsWith('-') || (trimmed.startsWith('*') && !trimmed.startsWith('**'))) {
        const content = trimmed.replace(/^[•\-\*]\s*/, '');
        return (
          <div key={i} className="flex gap-2 py-0.5 pl-1">
            <span className="text-blue-500 mt-0.5 shrink-0">•</span>
            <span>{renderInline(content)}</span>
          </div>
        );
      }

      // Číslovaný seznam
      if (trimmed.match(/^\d+\./)) {
        const num = trimmed.match(/^\d+\./)[0];
        const content = trimmed.replace(/^\d+\.\s*/, '');
        return (
          <div key={i} className="flex gap-2 py-0.5 pl-1">
            <span className="text-blue-500 font-semibold shrink-0 min-w-[1.25rem]">{num}</span>
            <span>{renderInline(content)}</span>
          </div>
        );
      }

      return <p key={i} className="py-0.5">{renderInline(trimmed)}</p>;
    });
  };

  const bg = isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const subText = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const tagBg = isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';

  return (
    <>
      {/* Overlay — schovaný při minimalizaci */}
      {!minimized && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Drawer s CSS transition */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-lg z-50 shadow-2xl flex flex-col ${bg} border-l ${border} transition-transform duration-300 ease-in-out ${minimized ? 'translate-x-full' : 'translate-x-0'}`}
      >
        {/* Minimize tab — vždy vyčnívá vlevo z draweru */}
        <button
          onClick={() => setMinimized(m => !m)}
          className={`absolute -left-10 top-1/2 -translate-y-1/2 w-10 flex flex-col items-center justify-center gap-1 py-4 rounded-l-xl shadow-lg border-l border-t border-b transition-colors z-10 ${isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200' : 'bg-teal-600 border-teal-700 hover:bg-teal-700 text-white'}`}
          title={minimized ? 'Zobrazit panel' : 'Minimalizovat panel'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 transition-transform duration-300 ${minimized ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {minimized && (
            <span className="text-xs font-bold [writing-mode:vertical-rl] rotate-180 mt-1 tracking-wide">
              🎯 Co nabídnout?
            </span>
          )}
        </button>

        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${border} shrink-0`}>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              🎯 Co nabídnout?
            </h2>
            <p className={`text-xs mt-0.5 ${subText}`}>
              {company?.name} · IČO {company?.ico}
            </p>
          </div>
          <div className="flex gap-2">
            {!loading && suggestion && (
              <button
                onClick={fetchSuggestion}
                className={`text-xs px-3 py-1.5 rounded-lg border transition ${isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}
                title="Načíst znovu"
              >
                🔄 Znovu
              </button>
            )}
            <button
              onClick={onClose}
              className={`text-xl leading-none px-2 py-1 rounded transition ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm">
          {loading && (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
              <p className={subText}>Načítám předměty podnikání z ARES a generuji návrhy…</p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-100 text-red-700 border border-red-300 rounded-lg p-4">
              <p className="font-semibold">Chyba</p>
              <p className="mt-1 text-sm">{error}</p>
              <button
                onClick={fetchSuggestion}
                className="mt-3 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs"
              >
                Zkusit znovu
              </button>
            </div>
          )}

          {!loading && !error && suggestion && (
            <>
              {/* AI suggestion */}
              <div className={`rounded-lg border p-4 mb-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-100'}`}>
                <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>🤖 Návrh AI</p>
                <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {renderSuggestion(suggestion)}
                </div>
              </div>

              {/* Business activities */}
              {activities.length > 0 && (
                <div>
                  <p className={`text-xs font-semibold mb-2 ${subText}`}>📋 Předměty podnikání (z ARES)</p>
                  <div className="flex flex-wrap gap-2">
                    {activities.map((a, i) => (
                      <span key={i} className={`text-xs px-2 py-1 rounded-full ${tagBg}`}>{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && !error && !suggestion && (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <p className={subText}>Klikni na tlačítko pro načtení návrhu.</p>
              <button
                onClick={fetchSuggestion}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                🎯 Načíst návrh
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
